/**
 * Earth structural integrity domain — atmosphere boundary tracker
 * (F-102: 이전 명칭 "ozone" 에서 일괄 변경. UI 표현 "대기권" 과 코드 정합).
 *
 * Theme: as larger meteors stack against Earth's gravity well, they put
 * stress on its structure. Each meteor that drifts past the atmosphere
 * radius and *settles* there for the dwell window deals damage equal
 * to a tier-scaled value (bigger tiers crush more). When integrity
 * hits zero, Earth collapses → game over.
 *
 * Pure logic — tests drive it deterministically.
 */

export interface IntegrityListener {
  (state: IntegrityState): void;
}

export interface BreachListener {
  (info: BreachInfo): void;
}

export interface IntegrityState {
  current: number;
  max: number;
}

export interface BreachInfo {
  /** id of the meteor that caused this breach. */
  meteorId: number;
  /** Tier index of the breaching meteor (used for FX scaling + damage trace). */
  tierIndex: number;
  /** Damage value subtracted from integrity for this breach. */
  damage: number;
  /** Integrity AFTER the breach was applied. */
  integrityAfter: number;
}

export interface AtmosphereOptions {
  /** Maximum integrity (lives). Default 100 for the stress-bar UX. */
  maxIntegrity?: number;
  /** Distance from Earth center beyond which a meteor is "outside the atmosphere". */
  atmosphereRadius: number;
  /** Speed below which a meteor is considered settled (world units / sec). */
  restSpeedThreshold?: number;
  /** Dwell time required for a settled-outside meteor to count as a breach (seconds). */
  breachDwellSec?: number;
  /**
   * Tier-scaled damage for a breach. Default function: tier 0 = 4 stress,
   * each tier adds 2.5, so tier 9 ≈ 26.5 → max-tier breaches roughly halve
   * the integrity bar in one event.
   */
  damageForTier?: (tierIndex: number) => number;
}

interface MeteorSnapshot {
  id: number;
  /** World-space distance from Earth center. */
  distance: number;
  /** Linear speed magnitude. */
  speed: number;
  /** Tier index — drives breach damage scaling. */
  tierIndex: number;
}

export interface AtmosphereTracker {
  /** Per-frame update — pass the current snapshot of every active meteor. */
  update(dt: number, meteors: ReadonlyArray<MeteorSnapshot>): void;
  state(): IntegrityState;
  /** True iff integrity has reached 0. */
  isCollapsed(): boolean;
  /** Reset to fresh integrity (used on restart and on victory → endless). */
  reset(): void;
  /** Snapshot of currently-dwelling meteors (for visual warning ring). */
  dwellingMeteorIds(): ReadonlyArray<number>;
  /** Subscribe to integrity changes (breach decrements, resets). */
  subscribeIntegrity(l: IntegrityListener): () => void;
  /** Subscribe to per-breach events. */
  subscribeBreach(l: BreachListener): () => void;
}

const DEFAULT_MAX = 100;
const DEFAULT_REST_SPEED = 0.6;
const DEFAULT_DWELL_SEC = 1.5;

const DEFAULT_DAMAGE_FOR_TIER = (tierIndex: number): number =>
  Math.max(2, Math.round(4 + tierIndex * 2.5));

export function createAtmosphereTracker(opts: AtmosphereOptions): AtmosphereTracker {
  const max = opts.maxIntegrity ?? DEFAULT_MAX;
  if (max <= 0) throw new Error("createAtmosphereTracker: maxIntegrity must be positive");
  const atmosphereRadius = opts.atmosphereRadius;
  if (atmosphereRadius <= 0)
    throw new Error("createAtmosphereTracker: atmosphereRadius must be positive");
  const restSpeed = opts.restSpeedThreshold ?? DEFAULT_REST_SPEED;
  const dwellSec = opts.breachDwellSec ?? DEFAULT_DWELL_SEC;
  const damageFor = opts.damageForTier ?? DEFAULT_DAMAGE_FOR_TIER;

  let current = max;
  /** Per-meteor accumulated dwell-outside-atmosphere-at-rest time (seconds). */
  const dwell = new Map<number, number>();
  /** Meteors that already counted as a breach this stuck-period — must
   * leave + re-enter the dwell condition before counting again. */
  const cooldown = new Set<number>();

  const integrityListeners = new Set<IntegrityListener>();
  const breachListeners = new Set<BreachListener>();

  const stateSnap = (): IntegrityState => ({ current, max });

  const announceIntegrity = (): void => {
    const s = stateSnap();
    for (const l of integrityListeners) l(s);
  };

  const update = (
    dt: number,
    meteors: ReadonlyArray<MeteorSnapshot>,
  ): void => {
    if (current <= 0) return;
    const seenIds = new Set<number>();
    for (const m of meteors) {
      seenIds.add(m.id);
      const outsideAtmosphere = m.distance > atmosphereRadius;
      const settled = m.speed < restSpeed;
      if (outsideAtmosphere && settled) {
        if (cooldown.has(m.id)) {
          continue;
        }
        const accumulated = (dwell.get(m.id) ?? 0) + dt;
        if (accumulated >= dwellSec) {
          dwell.delete(m.id);
          cooldown.add(m.id);
          const damage = damageFor(m.tierIndex);
          current = Math.max(0, current - damage);
          for (const l of breachListeners) {
            l({
              meteorId: m.id,
              tierIndex: m.tierIndex,
              damage,
              integrityAfter: current,
            });
          }
          announceIntegrity();
        } else {
          dwell.set(m.id, accumulated);
        }
      } else {
        if (dwell.has(m.id)) dwell.delete(m.id);
        if (cooldown.has(m.id)) cooldown.delete(m.id);
      }
    }
    for (const id of dwell.keys()) {
      if (!seenIds.has(id)) dwell.delete(id);
    }
    for (const id of cooldown) {
      if (!seenIds.has(id)) cooldown.delete(id);
    }
  };

  const dwellingMeteorIds = (): ReadonlyArray<number> => Array.from(dwell.keys());

  const reset = (): void => {
    dwell.clear();
    cooldown.clear();
    if (current !== max) {
      current = max;
      announceIntegrity();
    }
  };

  return {
    update,
    state: stateSnap,
    isCollapsed: () => current <= 0,
    reset,
    dwellingMeteorIds,
    subscribeIntegrity: (l) => {
      integrityListeners.add(l);
      l(stateSnap());
      return () => integrityListeners.delete(l);
    },
    subscribeBreach: (l) => {
      breachListeners.add(l);
      return () => breachListeners.delete(l);
    },
  };
}

export const __testing = {
  DEFAULT_MAX,
  DEFAULT_REST_SPEED,
  DEFAULT_DWELL_SEC,
  DEFAULT_DAMAGE_FOR_TIER,
};
